import { Component, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BelegEntry, DokuLinkEntry } from '../../../core/gb-api';
import { DokulinkModalComponent } from '../../../dokulinkModal/dokulink-modal.component';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { ReplaySubject, Subscription } from 'rxjs';
import { DokulinkItem } from './dokulinkItem';
import { sanatizedSaveAs } from '../../functions/file.functions';
import { GbPrintService } from '../../../core/services/app-print.service';
import { AsyncPipe } from '@angular/common';
import DokulinkVerfuegbarEnum = BelegEntry.DokulinkVerfuegbarEnum;

@Component({
  selector: 'cap-dokulink',
  templateUrl: './dokulink.component.html',
  styleUrls: ['./dokulink.component.scss'],
  imports: [NgbTooltip, AsyncPipe, TranslatePipe]
})
export class DokulinkComponent implements OnInit, OnDestroy {
  protected dokulinkItem: DokulinkItem;

  private dokulinkSubject = new ReplaySubject<DokulinkVerfuegbarEnum>();
  private subscription = new Subscription();

  protected _showTitle = false;
  private isModalOpen = false;

  @Input()
  set showTitle(val: boolean) {
    this._showTitle = val;
  }

  @Input()
  set item(value: DokulinkItem) {
    this.dokulinkItem = value;
  }

  @Output()
  dokulinkVerfuegbar = this.dokulinkSubject.asObservable();

  constructor(
    private modalService: NgbModal,
    private translate: TranslateService,
    private printService: GbPrintService
  ) {
    keysToTranslate<string>([
      'DokumenteZuGeschaeft',
      'DokumenteZuRecht',
      'DokumenteZuGrundstueck',
      'DokumenteZuBeleg',
      'DokumenteZuAlteGrundbuchmeldung'
    ]);
  }

  private static handleError(err) {
    if (err.status !== 403) {
      throw err;
    }
  }

  ngOnInit(): void {
    this.checkDokulinkVerfuegbar();
  }

  checkDokulinkVerfuegbar() {
    if (!this.dokulinkItem || !this.dokulinkItem.valid || !this.dokulinkItem.hasPermission) {
      this.setDokulinkVerfuegbar(false);
      return;
    }
    if (this.dokulinkItem.verfuegbar !== undefined) {
      this.setDokulinkVerfuegbar(this.dokulinkItem.verfuegbar === DokulinkVerfuegbarEnum.DOKULINK_VORHANDEN);
      return;
    }
    this.subscription.add(
      this.dokulinkItem.hasLink$.subscribe({
        next: (hasDokulink) => this.setDokulinkVerfuegbar(hasDokulink),
        error: (err) => {
          this.setDokulinkVerfuegbar(false);
          if (err.status !== 404) {
            DokulinkComponent.handleError(err);
          }
        }
      })
    );
  }

  private setDokulinkVerfuegbar(hasDokulink: Boolean) {
    this.dokulinkItem.verfuegbar = hasDokulink
      ? DokulinkVerfuegbarEnum.DOKULINK_VORHANDEN
      : DokulinkVerfuegbarEnum.DOKULINK_NICHT_VORHANDEN;
    this.dokulinkSubject.next(this.dokulinkItem.verfuegbar);
  }

  openDokuLink() {
    this.subscription.add(
      this.dokulinkItem.getLink$.subscribe((links) => {
        this.dokulinkItem.links = links;
        if (this.isSingleLinkAvailable(links)) {
          // open without dialog
          if (links.egvtDokuLinks.length === 0) {
            return window.open(links.dokuLinks[0].dokuLink);
          }
          return this.loadEgvtDokument(links.egvtDokuLinks[0]);
        }
        return this.openDokulinkDialog();
      })
    );
  }

  private isSingleLinkAvailable(links) {
    return this.getNumberOfDokulinks(links) + this.getNumberOfEgvtLinks(links) === 1;
  }

  private getNumberOfEgvtLinks(links) {
    return links.egvtDokuLinks ? links.egvtDokuLinks.length : 0;
  }

  private getNumberOfDokulinks(links) {
    return links.dokuLinks ? links.dokuLinks.length : 0;
  }

  openDokulinkDialog() {
    if (this.isModalOpen) {
      return;
    }
    this.isModalOpen = true;

    const modalRef = this.modalService.open(DokulinkModalComponent);
    modalRef.componentInstance.modalTitle = this.translate.instant('DokumenteZu' + this.dokulinkItem.type, {
      param: this.dokulinkItem.tooltipParam
    });
    modalRef.componentInstance.dokulinkItem = this.dokulinkItem;

    modalRef.result.finally(() => {
      this.isModalOpen = false;
    });

    return modalRef;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  loadEgvtDokument(link: DokuLinkEntry) {
    this.printService.getEgvtDokument(link.dokuLink).subscribe((data) => {
      let file: Blob & { name?: string };
      file = new Blob([data], { type: 'application/pdf' });
      file.name = link.dokumentName + '.pdf';
      sanatizedSaveAs(file);
    });
  }
}
